import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import AgeVerificationModal from "./components/AgeVerificationModal";
import DnaHelix from "./components/DnaHelix";
import DiscountPopup from "./components/DiscountPopup";
import LockScreen from "./components/LockScreen";
import AccessibilityWidget from "./components/AccessibilityWidget";

import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CheckoutPage from "./pages/CheckoutPage";
import ZellePaymentPage from "./pages/ZellePaymentPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import CheckoutCancelPage from "./pages/CheckoutCancelPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import WishlistPage from "./pages/WishlistPage";
import AboutPage from "./pages/AboutPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminProductsPage from "./pages/AdminProductsPage";
import AdminOrdersPage from "./pages/AdminOrdersPage";
import AdminCustomersPage from "./pages/AdminCustomersPage";
import AdminAffiliatesPage from "./pages/AdminAffiliatesPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminEmailsPage from "./pages/AdminEmailsPage";
import AdminPromoCodesPage from "./pages/AdminPromoCodesPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AffiliateSignupPage from "./pages/AffiliateSignupPage";
import AffiliateDashboardPage from "./pages/AffiliateDashboardPage";
import AffiliateTaxInfoPage from "./pages/AffiliateTaxInfoPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import { captureAffiliateRef } from "./utils/affiliateTracking";

function App() {
  // Track affiliate referrals from URL — persists in localStorage + first-party cookie for 180 days
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      captureAffiliateRef(ref);
    }
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
          <BrowserRouter>
            <LockScreen>
            <div className="min-h-screen flex flex-col relative" style={{ zIndex: 2 }}>
              <DnaHelix />
              <AgeVerificationModal />
              <DiscountPopup />
              <Toaster 
                position="top-right" 
                toastOptions={{
                  style: {
                    background: '#0C0E14',
                    border: '1px solid #1E2532',
                    color: '#ffffff',
                  },
                }}
              />
              <Routes>
                {/* Auth pages without navbar */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                
                {/* Pages with navbar and footer */}
                <Route
                  path="*"
                  element={
                    <>
                      <a href="#main-content" className="skip-to-content">Skip to content</a>
                      <Navbar />
                      <main id="main-content" className="flex-1">
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/products" element={<ProductsPage />} />
                          <Route path="/products/:id" element={<ProductDetailPage />} />
                          <Route path="/about" element={<AboutPage />} />
                          <Route path="/terms" element={<TermsPage />} />
                          <Route path="/privacy" element={<PrivacyPage />} />
                          <Route path="/admin-login" element={<AdminLoginPage />} />
                          
                          {/* Checkout - allow guest */}
                          <Route path="/checkout" element={<CheckoutPage />} />
                          <Route path="/checkout/zelle" element={<ZellePaymentPage />} />
                          <Route path="/pay" element={<ZellePaymentPage />} />
                          <Route
                            path="/checkout/success"
                            element={<CheckoutSuccessPage />}
                          />
                          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
                          <Route
                            path="/orders"
                            element={
                              <ProtectedRoute>
                                <OrdersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/orders/:id"
                            element={
                              <ProtectedRoute>
                                <OrderDetailPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/wishlist"
                            element={
                              <ProtectedRoute>
                                <WishlistPage />
                              </ProtectedRoute>
                            }
                          />
                          {/* Admin Routes */}
                          <Route
                            path="/admin"
                            element={
                              <ProtectedRoute>
                                <AdminDashboardPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/products"
                            element={
                              <ProtectedRoute>
                                <AdminProductsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/orders"
                            element={
                              <ProtectedRoute>
                                <AdminOrdersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/customers"
                            element={
                              <ProtectedRoute>
                                <AdminCustomersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/affiliates"
                            element={
                              <ProtectedRoute>
                                <AdminAffiliatesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/analytics"
                            element={
                              <ProtectedRoute>
                                <AdminAnalyticsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/emails"
                            element={
                              <ProtectedRoute>
                                <AdminEmailsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/promo-codes"
                            element={
                              <ProtectedRoute>
                                <AdminPromoCodesPage />
                              </ProtectedRoute>
                            }
                          />
                          {/* Affiliate Routes */}
                          <Route path="/affiliate" element={<AffiliateSignupPage />} />
                          <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
                          <Route path="/affiliate/tax-info" element={<AffiliateTaxInfoPage />} />

                          {/* Public order tracking */}
                          <Route path="/track" element={<TrackOrderPage />} />
                        </Routes>
                      </main>
                      <Footer />
                      <AccessibilityWidget />
                    </>
                  }
                />
              </Routes>
            </div>
            </LockScreen>
          </BrowserRouter>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
